import { connect } from 'react-redux';
import { ApplicationState } from "../../../store";
import { ToxnotComponentProperties } from "../BaseApplicationComponents/ToxnotComponent";
import * as SurveyResponseController from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import { SurveyResponseState } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates';
import { AISurveyAgentState, getActiveAIOperationFromState } from '../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';
import { LoadingSpinner, LoadingSpinnerSize } from '../LoadingSpinner';
import { ToxnotSignInRequiredComponent } from "../BaseApplicationComponents/ToxnotSignInRequiredComponent";
import { TeamRole } from '../../../store/Models/UserContext';
import { SelectedInventoriesManager } from '../../../store/Managers/SelectedInventoriesManager';
import { DataCollectionAiAgentOperationType, RevisionDetails, SurveyType } from '../../../store/Models/Surveys/SurveyDataModel';
import { isAiProcessingReadonly, isSurveyReadonly } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import RevisionRequest from '../RevisionRequest/RevisionRequest';
import React, { RefObject } from "react";
import MarkdownRenderer from '../MarkdownRenderer/MarkdownRenderer';
import ExtensionRequest from '../ExtensionRequest/extensionRequest';
import CommonModalEditor from '../Common/CommonModalEditor';
import AIButton from '../../common/AIButton';
import AIAnswerMetadata from '../AiAnswerMetadata/AIAnswerMetadata';
import { ToxnotServiceClient } from '../../../store/ToxnotServiceClient';
import { SdsInstructionBanner } from '../SdsInstructionBanner';

import "./styles.css";
import SdsIngredientsImport from '../SdsIngredientsImport';

interface LocalState {
    loading: boolean;
}

interface LocalProps {
    pageRevisionInstructions: RevisionDetails[];
}

type BomEditorProps =
    ToxnotComponentProperties &
    typeof SurveyResponseController.actionCreators &
    SurveyResponseState &
    { aiSurveyAgentState?: AISurveyAgentState } &
    LocalProps;

const BACK_TO_SURVEY_RESPONSE = "BACK_TO_SURVEY_RESPONSE";
const TRIGGER_VALIDATION = "TRIGGER_VALIDATION";
const BOM_DATA_LOADED = "BOM_DATA_LOADED";

class BomEditor extends ToxnotSignInRequiredComponent<BomEditorProps, LocalState> {
    private readonly iframeRef: RefObject<HTMLIFrameElement>;

    public constructor(props: BomEditorProps) {
        super(props);

        this.iframeRef = React.createRef();
    }

    public componentDidMount() {
        window.addEventListener('message', this.iframeComm);
        window.addEventListener('resize', this.resize);

        this.props.updateBomEditorPageVisited();
        void this.loadBomAiHelpfulFeedback();
    }

    public componentDidUpdate(prevProps: BomEditorProps) {
        const previousInventoryId = prevProps.selectedInventory?.inventoryId ?? null;
        const currentInventoryId = this.props.selectedInventory?.inventoryId ?? null;

        if (prevProps.responseId !== this.props.responseId || previousInventoryId !== currentInventoryId) {
            void this.loadBomAiHelpfulFeedback();
        }
    }

    public componentWillUnmount() {
        window.removeEventListener('message', this.iframeComm);
        window.removeEventListener('resize', this.resize);
    }

    private resize = () => this.forceUpdate();

    private iframeComm = (event: MessageEvent<any>) => {
        if (event.data === BACK_TO_SURVEY_RESPONSE) {
            if (event.source !== this.iframeRef.current?.contentWindow) {
                return;
            }

            this.setState({
                loading: true
            });

            // Refresh the survey state as well as the BOM after importing.
            window.location.reload();
        } else if (event.data === TRIGGER_VALIDATION && this.props?.selectedInventory?.inventoryId) {
            this.props.updateValidationResult([this.props?.selectedInventory?.inventoryId]);
        }
    }

    private disableBomFieldsIfSurveyIsSubmitted() {
        const isReadonly = isSurveyReadonly(this.props.coreData, getActiveAIOperationFromState(this.props.aiSurveyAgentState, DataCollectionAiAgentOperationType.Survey));

        const updateIframeDisabledState = () => {
            const iframe = document.getElementById('bom-editor-iframe') as HTMLIFrameElement;
            if (iframe?.contentWindow) {
                const editorRoot = iframe.contentWindow.document.querySelector('.editor-root');
                if (editorRoot) {
                    if (isReadonly) {
                        editorRoot.classList.add('disabled');
                    } else {
                        editorRoot.classList.remove('disabled');
                    }
                }
            }
        };

        const bomSpreasheetElem = $("#bom-editor-iframe").contents().find(".editor-root .product-spreadsheet-view");
        if (bomSpreasheetElem !== null && bomSpreasheetElem !== undefined && bomSpreasheetElem.length > 0) {
            updateIframeDisabledState();
        } else {
            const bomIframeComm = (event: MessageEvent<any>) => {
                if (event.data === BOM_DATA_LOADED) {
                    updateIframeDisabledState();
                    window.removeEventListener('message', bomIframeComm);
                }
            };
            window.addEventListener('message', bomIframeComm);
        }
    }

    private handleIframeLoad = () => {
        const iframe = this.iframeRef.current;

        let messageBody = JSON.stringify({
            regulationListIds: this.props?.selectedInventory?.applicableRegulatoryLists.selectMany(r => r.regulationListIds)
        });

        iframe?.contentWindow?.postMessage(messageBody, "*");
    }

    protected getDisallowedRoles(): TeamRole[] {
        return [TeamRole.Marketing, TeamRole.Viewer];
    }

    private setLocalStorageItems() {
        localStorage.setItem("select-inventories-action", "surveyResponseImport");
        localStorage.setItem("import-survey-response-id", this.props.responseId!);
        localStorage.setItem("import-survey-response-inventory-id", this.props.selectedInventory?.inventoryId!);
        localStorage.setItem("import-survey-response-return-url", window.location.href);
    }

    private handleClickImportMaterial = () => {
        //localStorage.setItem("customer-last-active-tab", "current-tab"); //todo : find the current tab
        this.setLocalStorageItems();

        SelectedInventoriesManager.navigateToSelectInventoriesPage(this.getUserContextForSignedInUser().userId, this.localizeString("Select Products and Materials"));
    }

    private showHideSdsIngredientButtonOnFileCount(fileCount: number){
        //show the button only if there are multiple-sds files
        if(fileCount > 1){
            $(".import-sds-bom-button").show();
        }
    }

    private refreshBomIngredientsTable(){
        if (this.iframeRef.current) {
            this.iframeRef.current.src = this.iframeRef.current.src;
        }
    }

    private getRootRowAiMetadata() {
        const rootRow = this.props.selectedInventory?.inventoryRows?.find(row => row.level === 0);
        return rootRow?.aiMetadata ?? null;
    }

    private hasBomAiResponse() {
        const aiMetadata = this.getRootRowAiMetadata();
        if (!aiMetadata) {
            return false;
        }

        const hasReasoning = (aiMetadata.reasoning ?? "").trim().length > 0;
        const hasSources = (aiMetadata.citations?.length ?? 0) > 0;
        const hasConfidence = aiMetadata.confidenceScoreDetails != null;
        const hasSuggestedValue = (aiMetadata.value ?? "").trim().length > 0;

        return hasReasoning || hasSources || hasConfidence || hasSuggestedValue;
    }

    private loadBomAiHelpfulFeedback = async () => {
        const responseId = this.props.responseId;
        const inventoryId = this.props.selectedInventory?.inventoryId;
        const rootRow = this.props.selectedInventory?.inventoryRows?.find(row => row.level === 0);

        if (!responseId || !inventoryId || !rootRow?.aiMetadata || !this.hasBomAiResponse()) {
            return;
        }

        try {
            const toxnotServiceClient = new ToxnotServiceClient();
            const result = await toxnotServiceClient.getAiBomHelpfulFeedback(responseId, inventoryId);

            if (!result.success || !result.data) {
                this.props.appServices?.trackEvent("survey.bom_ai_helpful_feedback_fetch_failed", {
                    apiResult: result.result,
                    responseId,
                    inventoryId
                });
                return;
            }

            if (this.props.responseId !== responseId || this.props.selectedInventory?.inventoryId !== inventoryId) {
                return;
            }

            const currentRootRow = this.props.selectedInventory?.inventoryRows?.find(row => row.level === 0);
            if (!currentRootRow?.aiMetadata) {
                return;
            }

            const normalizedIsHelpful = result.data.isHelpful > 0 ? 1 : result.data.isHelpful < 0 ? -1 : 0;
            if (currentRootRow.aiMetadata.isHelpful !== normalizedIsHelpful) {
                currentRootRow.aiMetadata.isHelpful = normalizedIsHelpful;
                this.forceUpdate();
            }
        } catch (error) {
            this.props.appServices?.trackEvent("survey.bom_ai_helpful_feedback_fetch_error", {
                responseId,
                inventoryId,
                error: error instanceof Error ? error.message : String(error)
            });
        }
    };

    private handleBomAiHelpfulChange = (isHelpful: -1 | 0 | 1) => {
        const rootRow = this.props.selectedInventory?.inventoryRows?.find(row => row.level === 0);
        if (rootRow?.aiMetadata) {
            rootRow.aiMetadata.isHelpful = isHelpful;
            this.forceUpdate();
        }

        const responseId = this.props.responseId;
        const inventoryId = this.props.selectedInventory?.inventoryId;

        if (!responseId || !inventoryId) {
            this.props.appServices?.trackEvent("survey.bom_ai_helpful_feedback_submit_missing_context", {
                responseId: responseId ?? null,
                inventoryId: inventoryId ?? null,
                isHelpful
            });
            return;
        }

        void (async () => {
            try {
                const toxnotServiceClient = new ToxnotServiceClient();
                const result = await toxnotServiceClient.aiBomHelpfulFeedback({
                    responseId,
                    inventoryId,
                    isHelpful
                });

                if (!result.success) {
                    this.props.appServices?.trackEvent("survey.bom_ai_helpful_feedback_submit_failed", {
                        apiResult: result.result,
                        responseId,
                        inventoryId,
                        isHelpful
                    });
                }
            } catch (error) {
                this.props.appServices?.trackEvent("survey.bom_ai_helpful_feedback_submit_error", {
                    responseId,
                    inventoryId,
                    isHelpful,
                    error: error instanceof Error ? error.message : String(error)
                });
            }
        })();
    }

    protected renderConcreteComponent(): JSX.Element | null {
        this.disableBomFieldsIfSurveyIsSubmitted();
        const currentExtensionRequest = this.props.coreData?.extensionRequest;
        const canAnswer = !currentExtensionRequest?.inventoryIds?.some(i => i === this.props.selectedInventory?.inventoryId);
        const isReadonlyDueToAi = isAiProcessingReadonly(getActiveAIOperationFromState(this.props.aiSurveyAgentState, DataCollectionAiAgentOperationType.Survey));
        const src = `/Data/Inventory?responseId=${this.props.responseId}&inventoryId=${this.props.selectedInventory?.inventoryId}&bomEditorIframe=true&mode=EditSurveySupplierResponseBom&canAnswer=${canAnswer}`;
        const rootRowAiMetadata = this.getRootRowAiMetadata();

        return (
            <>
                <div className="page-title-border m-b-10 flex-space-between">
                    <h4 className="m-t-10 m-b-10">
                        {this.localizeString("Ingredient Disclosure Data")}
                        {!this.props.coreData?.request?.fields.some(f => f.requiredInLeafRows || f.requiredInNonSummaryNonLeafRows || f.requiredInSummaryRow) &&
                            <span className="badge m-l-5">{this.localizeString("Optional")}</span>
                        }
                    </h4>
                    <div className="import-container">
                        <div className="m-b-10">
                            <AIButton pageContext="ingredient-disclosure" className="m-r-5" />
                            {this.props.selectedInventory?.hasBomPage &&
                                <>
                                    <button className="btn btn-default m-r-5 import-sds-bom-button initially-hidden" onClick={() => { $("#import-sds-btns-modal-bom-editor").modal("show"); }} disabled={isReadonlyDueToAi}>
                                        <span className="glyphicon glyphicon-plus"></span>{this.localizeString("Add SDS Ingredients")}
                                    </button>
                                    <SdsIngredientsImport
                                        modalId="import-sds-btns-modal-bom-editor"
                                        localizer={this.props.appServices}
                                        inventoryId={this.props.selectedInventory.inventoryId}
                                        onFileCountUpdate={this.showHideSdsIngredientButtonOnFileCount}
                                        refreshParentPage={(success) => (success === true ? this.refreshBomIngredientsTable() : null)}
                                    />
                            
                                    <button className="btn btn-default m-r-5 import-button" onClick={() => { $("#import-btns-modal-bom-editor").modal("show"); }} disabled={isReadonlyDueToAi}>{this.localizeString("Import")}
                                        <span className="import-button-icon glyphicon glyphicon-download-alt">
                                        </span>
                                    </button>
                                    <CommonModalEditor modalId={"import-btns-modal-bom-editor"} localizer={this.props.appServices} responseId={this.props.responseId} selectedInventory={this.props.selectedInventory} userId={this.getUserContextForSignedInUser().userId} navContext={undefined} handleClickImportMaterial={this.handleClickImportMaterial}></CommonModalEditor>
                                </>
                            }
                            {this.props.coreData?.request?.allowExtensionRequest && this.props.selectedInventory?.revisionRequest == null &&
                                this.props.coreData?.request?.surveyType !== SurveyType.RequestMaterialData_AllowSupplierToSpecifyMaterials &&
                                Object.entries(this.props.coreData?.response?.groupedResponseInventories).every(g => g[1].groupInventoryId !== this.props.selectedInventory?.inventoryId) &&
                                    <ExtensionRequest
                                        inventoryId={this.props.selectedInventory?.inventoryId}
                                        extensionRequest={this.props.coreData?.extensionRequest}
                                        localizer={this.props.appServices}
                                        responseId={this.props.coreData?.response.responseId}
                                        responseOwnerId={this.props.coreData?.response.ownerId}
                                        updateExtensionRequest={this.props.setExtensionRequestAndValidateProduct}
                                        setSupplierActionUnavailable={this.props.setSupplierActionUnavailable}
                                        validationType={this.props.selectedInventory?.validationType}>
                                    </ExtensionRequest>
                            }
                        </div>
                    </div>
                </div>
                {this.state?.loading &&
                    <div className="text-center">
                        <LoadingSpinner size={LoadingSpinnerSize.Large} />
                    </div>
                }
                {!this.state?.loading &&
                    <>
                        {/* SDS Instruction Text */}
                        {this.props.coreData?.request?.sdsRequested && 
                            <SdsInstructionBanner 
                                fileList={this.props.selectedInventory?.fileList}
                                pageType="BomEditor"
                            />
                        }
                        <div className="survey-bom-instructions" ref={this.afterRevisionsAndInstructionsRender}>
                            <MarkdownRenderer htmlText={this.localizeString("Create a list of ingredients by adding rows and selecting a Chemical Abstracts Service (CAS) Registry Number or select an import option. You can click the [eye icon](http://help.exchange.3eco.com/en/articles/2420330-marking-a-chemical-proprietary-in-a-supplier-survey) to make a CAS Number Proprietary and it will not be shared. 3E Exchange will automatically screen for regulated substances. Red asterisks indicate required fields.")} />
                        </div>
                        {rootRowAiMetadata &&
                            <AIAnswerMetadata
                                localizer={this.props.appServices}
                                aiMetadata={rootRowAiMetadata}
                                currentValue={rootRowAiMetadata.value}
                                isDisabled={isReadonlyDueToAi}
                                shouldShowModifiedState={false}
                                shouldShowRevertButton={false}
                                containerClassName="ai-suggested-bom-data"
                                detailsTopMessage={{
                                    variant: "bom",
                                    title: this.localizeString("AI-Generated Ingredient Data"),
                                    message: this.localizeString("The ingredient information below has been automatically extracted and verified from uploaded documentation."),
                                    note: this.localizeString("Any BOM content row at level 8 or above will be adjusted to level 7."),
                                    noteItalicized: true
                                }}
                                onHelpfulSelectionChange={this.handleBomAiHelpfulChange}
                                onExpandedChange={this.afterRevisionsAndInstructionsRender}
                            />
                        }
                        {this.props.pageRevisionInstructions && this.props.pageRevisionInstructions.length > 0 &&
                            <RevisionRequest key="bom-revision-request" localizer={this.props.appServices} afterRenderCallback={this.afterRevisionsAndInstructionsRender} revisionRequestInstructionsList={this.props.pageRevisionInstructions} revisionRequestTitle={this.localizeString("Revision Requests:")} extensibleColumnNames={(this.props.selectedInventory?.extensibleColumnNames ?? [])}></RevisionRequest>
                        }
                        <div className={`survey-response-bom-iframe-wrapper ${isSurveyReadonly(this.props.coreData, getActiveAIOperationFromState(this.props.aiSurveyAgentState, DataCollectionAiAgentOperationType.Survey)) ? "survey-response-bom-disabled" : ""}`} style={{ top: this.getTopBasedOnRevisionAndInstructions() }}>
                            <iframe ref={this.iframeRef} onLoad={this.handleIframeLoad} id="bom-editor-iframe" className="bomEditorIframe" src={src} frameBorder="0"></iframe>
                        </div>
                    </>
                }
            </>
        );
    }

    private afterRevisionsAndInstructionsRender = () => {
        const top = this.getTopBasedOnRevisionAndInstructions();

        $(".survey-response-bom-iframe-wrapper").css("top", top);
    }

    private getTopBasedOnRevisionAndInstructions() {
        const revisionInstructionsContainers = $(".survey-revision-request-wrapper");
        const surveyBomInstructionsContainer = $(".survey-bom-instructions");
        const aiSuggestedBomDataContainer = $(".ai-answer-field__combined-wrapper.ai-suggested-bom-data");
        const pageHeading = $(".survey-response-body-wrapper .page-title-border");
        const sdsInstructionBanner = $(".sds-instruction-banner");

        let bomInstructionsHeight = 0;
        let revisionRequestHeight = 0;
        let pageHeadingHeight = 0;
        let aiSuggestedBomDataHeight = 0;
        let sdsBannerHeight = 0;

        revisionInstructionsContainers?.each((_, object) => {
            revisionRequestHeight = revisionRequestHeight + object.clientHeight + 10; // 10 represents margin bottom of the revision wrapper
        });

        if (surveyBomInstructionsContainer.length > 0) {
            bomInstructionsHeight = surveyBomInstructionsContainer[0].clientHeight + 20; //margin bottom for BOM instructions
        }

        if (aiSuggestedBomDataContainer.length > 0) {
            aiSuggestedBomDataHeight = aiSuggestedBomDataContainer.first().outerHeight(true) ?? 0;
        }

        if (pageHeading.length > 0) {
            pageHeadingHeight = pageHeading[0].clientHeight + 10; //margin bottom for page heading
        }

         if (sdsInstructionBanner.length > 0) {
            sdsBannerHeight = sdsInstructionBanner[0].clientHeight + 15; //margin bottom for SDS banner
        }
        return `${revisionRequestHeight + bomInstructionsHeight + pageHeadingHeight + sdsBannerHeight + aiSuggestedBomDataHeight}px`;
    }
}

export default connect(
    (state: ApplicationState) => ({ ...state.surveyResponseState, aiSurveyAgentState: state.aiSurveyAgentState, appServices: state.appServices }),
    SurveyResponseController.actionCreators
)(BomEditor as any);
